import React from 'react';
import './Guides.css';

export default function ServoGuide() {
  return (
    <div className="guide-container">
      <h1 className="guide-header">🦾 How to Use a Servo Motor with Arduino</h1>

      <div className="guide-body">
        <h2>🦾 Precise Motion with Servo Motors</h2>
        <p>
          Servos are fantastic for precise motion control in robotics! Unlike regular DC motors that spin continuously, 
          servo motors can move to specific angles and hold their position. They're perfect for robot arms, steering 
          mechanisms, and any project requiring accurate positioning.
        </p>

        <h2>🧰 What You'll Need:</h2>
        <ul className="components-list">
          <li>Arduino Uno or Nano</li>
          <li>SG90 or MG90S Servo Motor (9g micro servo)</li>
          <li>Jumper wires (3 pieces minimum)</li>
          <li>External 5V power supply (recommended for multiple servos)</li>
          <li>Breadboard (optional for cleaner connections)</li>
        </ul>

        <div className="info-box">
          <p>💡 <strong>Servo vs DC Motor:</strong> Servos have built-in position feedback and control circuits. They can move to specific angles (0-180°) and stay there, while DC motors just spin continuously until told to stop.</p>
        </div>

        <h2>🔌 Wiring Guide:</h2>
        <div className="wiring-steps">
          <p>Servo motors have three wires with standard color coding:</p>
          <ul>
            <li><strong>Red wire</strong> → Arduino <strong>5V</strong> (power)</li>
            <li><strong>Brown/Black wire</strong> → Arduino <strong>GND</strong> (ground)</li>
            <li><strong>Orange/Yellow/White wire</strong> → Arduino <strong>pin 9</strong> (signal/control)</li>
          </ul>
        </div>

        <div className="warning-box">
          <p>⚠️ <strong>Power Consideration:</strong> A single small servo can usually run off Arduino's 5V pin, but for multiple servos or larger ones, use an external 5-6V power supply connected to the red wire, with grounds connected together.</p>
        </div>

        <h2>💻 Basic Servo Control Code:</h2>
        <div className="code-block">
          <pre>
{`#include <Servo.h>  // Include the servo library

Servo myServo;  // Create a servo object

void setup() {
  myServo.attach(9);  // Attach servo to pin 9
  Serial.begin(9600);
  Serial.println("Servo Control Ready!");
}

void loop() {
  Serial.println("Moving to 0 degrees");
  myServo.write(0);    // Move to 0 degrees
  delay(1000);         // Wait 1 second
  
  Serial.println("Moving to 90 degrees");
  myServo.write(90);   // Move to 90 degrees (center)
  delay(1000);
  
  Serial.println("Moving to 180 degrees");
  myServo.write(180);  // Move to 180 degrees
  delay(1000);
  
  Serial.println("Back to center");
  myServo.write(90);   // Return to center
  delay(1000);
}`}
          </pre>
        </div>

        <h2>🎯 Smooth Movement Code:</h2>
        <p>For smoother, more natural movement, try this sweep function:</p>
        
        <div className="code-block">
          <pre>
{`#include <Servo.h>

Servo myServo;
int pos = 0;  // Variable to store servo position

void setup() {
  myServo.attach(9);
  Serial.begin(9600);
}

void loop() {
  // Sweep from 0 to 180 degrees
  for (pos = 0; pos <= 180; pos += 1) {
    myServo.write(pos);
    delay(15);  // Small delay for smooth movement
  }
  
  // Sweep back from 180 to 0 degrees
  for (pos = 180; pos >= 0; pos -= 1) {
    myServo.write(pos);
    delay(15);
  }
}`}
          </pre>
        </div>

        <h2>🎮 Interactive Control:</h2>
        <p>Control your servo through the Serial Monitor:</p>
        
        <div className="code-block">
          <pre>
{`#include <Servo.h>

Servo myServo;

void setup() {
  myServo.attach(9);
  Serial.begin(9600);
  Serial.println("Enter angle (0-180):");
}

void loop() {
  if (Serial.available() > 0) {
    int angle = Serial.parseInt();
    
    if (angle >= 0 && angle <= 180) {
      myServo.write(angle);
      Serial.print("Moved to: ");
      Serial.print(angle);
      Serial.println(" degrees");
    } else {
      Serial.println("Please enter angle between 0-180");
    }
  }
}`}
          </pre>
        </div>

        <h2>🧪 Understanding Servo Control:</h2>
        <div className="explanation-box">
          <ul>
            <li><strong>myServo.attach(pin):</strong> Tells Arduino which pin controls the servo</li>
            <li><strong>myServo.write(angle):</strong> Commands servo to move to specific angle (0-180°)</li>
            <li><strong>Servo Library:</strong> Handles the complex PWM signals servos need</li>
            <li><strong>Position Holding:</strong> Servos automatically maintain their position</li>
            <li><strong>180° Range:</strong> Most hobby servos can rotate from 0° to 180° (half circle)</li>
          </ul>
        </div>

        <h2>⚙️ Advanced Tips:</h2>
        <div className="tips-box">
          <ul>
            <li><strong>Centering:</strong> Most servos consider 90° as their center position</li>
            <li><strong>Speed:</strong> Servos move at their own speed; you can't control how fast they turn</li>
            <li><strong>Continuous Servos:</strong> Some servos are modified to rotate continuously like DC motors</li>
            <li><strong>Precision:</strong> Good servos can position within 1-2 degrees accuracy</li>
            <li><strong>Power Draw:</strong> Servos use more power when fighting against loads</li>
          </ul>
        </div>

        <h2>🔧 Common Issues & Solutions:</h2>
        <div className="troubleshoot-box">
          <ul>
            <li><strong>Servo jitters/vibrates:</strong> Check power supply and connections</li>
            <li><strong>Won't move to exact angle:</strong> Some servos have slight calibration differences</li>
            <li><strong>Makes noise but doesn't move:</strong> May be stalled by physical obstruction</li>
            <li><strong>Moves erratically:</strong> Ensure stable 5V power and good ground connection</li>
            <li><strong>Gets hot:</strong> Reduce holding time or check for mechanical binding</li>
          </ul>
        </div>

        <div className="success-box">
          <p>🎉 <strong>Excellent work!</strong> You've mastered servo motor control! Servos are incredibly versatile and you'll use them in many robotics projects from simple pan-tilt cameras to complex robot arms.</p>
        </div>

        <h2>🚀 Project Ideas:</h2>
        <p>Put your new servo skills to work with these exciting projects:</p>
        <ul>
          <li>Build a robotic arm that can pick up small objects</li>
          <li>Create a pet feeder that opens at specific times</li>
          <li>Make a solar tracker that follows the sun</li>
          <li>Design a camera mount that can pan and tilt</li>
          <li>Build a walking robot using multiple servos for legs</li>
        </ul>
      </div>
    </div>
  );
}